import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Cancel as CancelIcon,
  CheckCircle as ReadyIcon,
  History as HistoryIcon,
  ThumbUp as ApproveIcon,
  SwapHoriz as MoveClientIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import DeliverActionIcon from '../components/icons/DeliverActionIcon';
import Layout from '../components/Layout';
import CertificateLink from '../components/CertificateLink';
import { AuditLogFromCell, AuditLogToCell, AuditLogClientLinkCell } from '../components/AuditLogTableCells';
import { formatDateTime } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { useOpenOrdersCount } from '../context/OpenOrdersCountContext';
import { ROLES } from '../constants/roles';
import { ORDER_STATUSES, ORDER_GROUP_STATUSES, DIAMOND_TYPES } from '../constants/appConstants';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import {
  getItemRef,
  getItemLab,
  getItemCarat,
  getItemDetails,
  getCertifiedDiamondSpecsWithWeight,
  getOrderItemDescriptionCell,
  sortOrderItemsByCreatedAt,
  getItemDisplayPrice,
  getItemPricePerCt,
  getItemTypeLabel,
  getItemExportTypeLabel,
  isCustomByWeightItem,
  isNonCertByCountItem,
  isEstimatedNonCertWeight,
  getNonCertByCountStoneLabel,
  getNonCertWeightLabel,
  getClientDisplayName,
} from '../utils/orderItemDisplay';
import { weightUnitShort } from '../utils/customItemPricing';
import ExportPdfButton from '../components/ExportPdfButton';
import ExportCsvButton from '../components/ExportCsvButton';
import OrderItemNotes from '../components/OrderItemNotes';
import ListNavigation from '../components/ListNavigation';
import MoveOrderClientDialog from '../components/MoveOrderClientDialog';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useFeedback } from '../context/FeedbackContext';
import ClientTagsRow from '../components/clients/ClientTagsRow';
import { getBackTarget, mergeNavState, getClientTabPath } from '../utils/navigationReturn';
import CheckoutLeftoversDialog from '../components/cart/CheckoutLeftoversDialog';
import { useCheckoutLeftoversNav } from '../hooks/useCheckoutLeftoversNav';
import BulkCancelOrderItemsDialog from '../components/orderItems/BulkCancelOrderItemsDialog';
import {
  cancelOrderItems,
  defaultReturnToInventoryForCancel,
  isOrderItemCancellable,
} from '../utils/orderItemCancel';
import { buttonStartIcon, iconButtonContent } from '../utils/buttonLoading';
import {
  formatTotalsByCurrency,
  sumFinalPriceByCurrency,
  toggleClientItemsPageSelection,
} from '../utils/clientItemsSelection';

function OrderDetailPageShell({ embedded, children }) {
  if (embedded) {
    return <Box>{children}</Box>;
  }
  return (
    <Layout>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
        {children}
      </Container>
    </Layout>
  );
}

const OrderDetail = ({ embedded = false, orderId: orderIdProp, clientId: clientIdProp }) => {
  const { t } = useTranslation();
  const { confirm, showError, showSuccess } = useFeedback();
  const { id: routeOrderId } = useParams();
  const id = orderIdProp || routeOrderId;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isInternal, userRole, customPermissions } = useAuth();
  const { refreshOpenOrdersCount } = useOpenOrdersCount();

  const { leftoversOpen, leftoversRows, closeLeftovers } = useCheckoutLeftoversNav();
  const listNav = location.state || {};
  const navIds = listNav.ids || [];
  const navIndex = navIds.length > 0 ? navIds.indexOf(id) : -1;
  const backTarget = embedded && clientIdProp
    ? { path: getClientTabPath(clientIdProp, 'orders'), labelKey: 'clients.backToOrders' }
    : getBackTarget(listNav, { path: '/orders', labelKey: 'orders.backToOrders' });

  const goBack = () => {
    navigate(backTarget.path, backTarget.state ? { state: backTarget.state } : undefined);
  };

  const goToPrev = () => {
    if (navIndex <= 0) return;
    const targetId = navIds[navIndex - 1];
    const path = embedded && clientIdProp
      ? getClientTabPath(clientIdProp, 'orders', targetId)
      : `/orders/${targetId}`;
    navigate(path, {
      state: mergeNavState({ ids: navIds, index: navIndex - 1 }, listNav),
    });
  };

  const goToNext = () => {
    if (navIndex < 0 || navIndex >= navIds.length - 1) return;
    const targetId = navIds[navIndex + 1];
    const path = embedded && clientIdProp
      ? getClientTabPath(clientIdProp, 'orders', targetId)
      : `/orders/${targetId}`;
    navigate(path, {
      state: mergeNavState({ ids: navIds, index: navIndex + 1 }, listNav),
    });
  };

  const [detailGroup, setDetailGroup] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailTab, setDetailTab] = useState(location.state?.tab === 1 ? 1 : 0);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [itemAuditOpen, setItemAuditOpen] = useState(false);
  const [itemAuditLogs, setItemAuditLogs] = useState([]);
  const [itemAuditLoading, setItemAuditLoading] = useState(false);
  const [itemAuditRef, setItemAuditRef] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelItems, setCancelItems] = useState([]);
  const [noteEditValue, setNoteEditValue] = useState('');
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [itemEdits, setItemEdits] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [moveClientDialogOpen, setMoveClientDialogOpen] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const { loading: groupActionLoading, run: runGroupAction } = useAsyncAction();
  const { run: runItemAction, isPending } = useAsyncAction();

  const canChangeStatus = isInternal && (
    userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('change_status'))
  );
  const canApproveReservations = isInternal && (
    userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('approve_reservations'))
  );
  const canEditOrderItemPrice = isInternal && (
    userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('edit_order_item_price'))
  );
  const canEditOrderItemWeight = isInternal && (
    userRole === ROLES.MANAGER || (customPermissions && customPermissions.includes('edit_order_item_weight'))
  );

  const canCancelOrderDetailItem = useCallback((item) => {
    if (!isOrderItemCancellable(item)) return false;
    if (canApproveReservations && item.status === ORDER_STATUSES.REQUESTED) return true;
    if (canChangeStatus && [ORDER_STATUSES.APPROVED, ORDER_STATUSES.READY, ORDER_STATUSES.MEMO].includes(item.status)) {
      return true;
    }
    return false;
  }, [canApproveReservations, canChangeStatus]);

  const canSelectOrderItems = isInternal && detailItems.some(canCancelOrderDetailItem);

  const selectedOrderItems = useMemo(
    () => detailItems.filter((i) => selectedItemIds.has(i._id)),
    [detailItems, selectedItemIds],
  );

  const selectedCancellableItems = useMemo(
    () => selectedOrderItems.filter(canCancelOrderDetailItem),
    [selectedOrderItems, canCancelOrderDetailItem],
  );

  const selectedSubtotalLabel = useMemo(
    () => formatTotalsByCurrency(sumFinalPriceByCurrency(selectedOrderItems), formatCurrency),
    [selectedOrderItems],
  );

  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [id]);

  useEffect(() => {
    setSelectedItemIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(detailItems.map((i) => i._id));
      const next = new Set([...prev].filter((itemId) => valid.has(itemId)));
      return next.size === prev.size ? prev : next;
    });
  }, [detailItems]);

  const toggleOrderItemSelect = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAllOrderItems = () => {
    setSelectedItemIds(toggleClientItemsPageSelection(
      detailItems.filter(canCancelOrderDetailItem),
      selectedItemIds,
    ));
  };

  const allOrderItemsSelected = detailItems.filter(canCancelOrderDetailItem).length > 0
    && detailItems.filter(canCancelOrderDetailItem).every((i) => selectedItemIds.has(i._id));
  const someOrderItemsSelected = selectedItemIds.size > 0 && !allOrderItemsSelected;
  const canTransferOrderClient = isInternal && (
    userRole === ROLES.MANAGER
    || (customPermissions && (
      customPermissions.includes('manage_orders')
      || customPermissions.includes('edit_order_item_price')
      || customPermissions.includes('edit_order_item_weight')
    ))
  );

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`/api/orders/${id}`);
      setDetailGroup(res.data.order);
      setNoteEditValue(res.data.order?.clientNote || '');
      setDetailItems(res.data.items || []);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      setDetailGroup(null);
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
      refreshOpenOrdersCount();
    }
  }, [id, refreshOpenOrdersCount]);

  const handleSaveNote = async () => {
    setNoteSaving(true);
    try {
      await axios.patch(`/api/orders/${id}/note`, { clientNote: noteEditValue });
      setDetailGroup((prev) => ({ ...prev, clientNote: noteEditValue.trim() || undefined }));
      setNoteEditing(false);
      showSuccess(t('orders.noteSaved'));
    } catch (err) {
      showError(t('common.saveFailed'));
    } finally {
      setNoteSaving(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const fetchAuditLogs = useCallback(async (groupId) => {
    setAuditLoading(true);
    try {
      const res = await axios.get('/api/audit-logs', { params: { order: groupId, limit: 200 } });
      setAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detailTab === 1 && detailGroup && auditLogs.length === 0) {
      fetchAuditLogs(detailGroup._id);
    }
  }, [detailTab, detailGroup, auditLogs.length, fetchAuditLogs]);

  const handleDetailTabChange = (event, newValue) => {
    setDetailTab(newValue);
    if (newValue === 1 && detailGroup) {
      fetchAuditLogs(detailGroup._id);
    }
  };

  const refreshDetailIfOpen = useCallback(async (groupId) => {
    if (groupId === id) {
      const res = await axios.get(`/api/orders/${groupId}`);
      setDetailGroup(res.data.order);
      setDetailItems(res.data.items || []);
      refreshOpenOrdersCount();
    }
  }, [id, refreshOpenOrdersCount]);

  // ──── Helpers ────
  const getGroupStatusColor = (status) => {
    switch (status) {
      case ORDER_GROUP_STATUSES.OPEN: return 'primary';
      case ORDER_GROUP_STATUSES.READY: return 'success';
      case ORDER_GROUP_STATUSES.MEMO: return 'warning';
      case ORDER_GROUP_STATUSES.PAID: return 'success';
      case ORDER_GROUP_STATUSES.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getGroupStatusLabel = (status) => {
    const labels = {
      [ORDER_GROUP_STATUSES.OPEN]: 'Open',
      [ORDER_GROUP_STATUSES.READY]: 'Ready',
      [ORDER_GROUP_STATUSES.MEMO]: t('status.memo'),
      [ORDER_GROUP_STATUSES.PAID]: 'Paid',
      [ORDER_GROUP_STATUSES.CANCELLED]: 'Cancelled',
    };
    return labels[status] || status;
  };

  const getGroupClientName = (group) => {
    const u = group?.clientUser;
    if (u) return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
    return group?.company?.name || '—';
  };

  const getGroupDate = (group) => (group?.createdAt ? formatDateTime(group.createdAt) : '—');
  const getGroupOrderNumber = (group) => (group ? '#' + group._id.slice(-6).toUpperCase() : '');
  const getCreatedByName = (group) => {
    const u = group?.createdBy;
    if (!u) return '—';
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
  };

  const getItemStatusColor = (status) => {
    switch (status) {
      case ORDER_STATUSES.REQUESTED: return 'warning';
      case ORDER_STATUSES.APPROVED: return 'info';
      case ORDER_STATUSES.READY: return 'primary';
      case ORDER_STATUSES.MEMO: return 'warning';
      case ORDER_STATUSES.PAID: return 'success';
      case ORDER_STATUSES.CANCELED: return 'default';
      case ORDER_STATUSES.EXPIRED: return 'default';
      default: return 'default';
    }
  };

  const getItemStatusLabel = (status) => {
    if (!status) return '';
    const labels = {
      [ORDER_STATUSES.REQUESTED]: 'Requested',
      [ORDER_STATUSES.APPROVED]: 'Approved',
      [ORDER_STATUSES.READY]: 'Ready',
      [ORDER_STATUSES.CANCELED]: 'Cancelled',
      [ORDER_STATUSES.EXPIRED]: 'Expired',
      [ORDER_STATUSES.MEMO]: t('status.memo'),
      [ORDER_STATUSES.PAID]: 'Paid',
    };
    return labels[status] || status;
  };

  const canClientCancel = (group) =>
    !isInternal && group?.status === ORDER_GROUP_STATUSES.OPEN;

  // ──── Item price/weight edit ────
  const handleSaveItemEdit = async (item) => {
    const edits = itemEdits[item._id];
    if (!edits || (edits.pricePerUnit === undefined && edits.weight === undefined)) return;
    setSavingItemId(item._id);
    try {
      const payload = {};
      if (edits.pricePerUnit !== undefined) payload.pricePerUnit = Number(edits.pricePerUnit);
      if ((item.type === DIAMOND_TYPES.NON_CERTIFIED || isCustomByWeightItem(item)) && edits.weight !== undefined) {
        payload.weight = Number(edits.weight);
      }
      await axios.patch(`/api/orders/${id}/items/${item._id}`, payload);
      setItemEdits((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
      await fetchDetail();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to save');
    } finally {
      setSavingItemId(null);
    }
  };

  const setItemEdit = (itemId, field, value) => {
    setItemEdits((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  // ──── Item actions ────
  const handleItemApprove = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/items/${itemId}/approve`);
        await refreshDetailIfOpen(groupId);
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to approve item');
      }
    }, `approve-${itemId}`);
  };

  const handleItemReady = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/items/${itemId}/ready`);
        await refreshDetailIfOpen(groupId);
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to mark item ready');
      }
    }, `ready-${itemId}`);
  };

  const handleItemToDelivered = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.patch(`/api/orders/${groupId}/items/${itemId}/status`, {
          status: ORDER_STATUSES.MEMO,
        });
        await refreshDetailIfOpen(groupId);
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToMarkDelivered'));
      }
    }, `deliver-${itemId}`);
  };

  const handleItemUndoDelivered = async (groupId, itemId) => {
    await runItemAction(async () => {
      try {
        await axios.patch(`/api/orders/${groupId}/items/${itemId}/status`, {
          status: ORDER_STATUSES.READY,
        });
        await refreshDetailIfOpen(groupId);
      } catch (error) {
        showError(error.response?.data?.error || t('orders.undoDeliveredFailed'));
      }
    }, `undo-deliver-${itemId}`);
  };

  const openCancelDialog = async (type, groupId, itemId = null) => {
    setCancelDialogOpen(true);
    if (type === 'group') {
      setCancelTarget({ type, groupId, itemId });
      try {
        const res = await axios.get(`/api/orders/${groupId}`);
        const items = (res.data.items || []).filter(
          (i) => i.status !== ORDER_STATUSES.CANCELED && i.status !== ORDER_STATUSES.EXPIRED
        );
        setCancelItems(items.map((i) => ({
          ...i,
          returnToInventory: defaultReturnToInventoryForCancel(i),
        })));
      } catch {
        setCancelItems([]);
      }
    } else {
      const existing = detailItems.find((i) => i._id === itemId);
      setCancelItems(existing ? [{
        ...existing,
        returnToInventory: defaultReturnToInventoryForCancel(existing),
      }] : []);
      setCancelTarget({ type: 'items', groupId, itemId });
    }
  };

  const openBulkCancelSelected = () => {
    const items = selectedCancellableItems.map((item) => ({
      ...item,
      returnToInventory: defaultReturnToInventoryForCancel(item),
    }));
    if (!items.length) return;
    setCancelTarget({ type: 'items', groupId: detailGroup?._id });
    setCancelItems(items);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelTarget(null);
    setCancelItems([]);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      if (cancelTarget.type === 'group') {
        const returnItemIds = cancelItems.filter((i) => i.returnToInventory).map((i) => i._id);
        await axios.patch(`/api/orders/${cancelTarget.groupId}/status`, {
          status: ORDER_GROUP_STATUSES.CANCELLED,
          returnItemIds,
        });
      } else if (cancelTarget.type === 'items') {
        await cancelOrderItems(cancelItems);
        setSelectedItemIds(new Set());
        showSuccess(t('checks.cancelSelectedSuccess', { count: cancelItems.length }));
      }
      closeCancelDialog();
      await fetchDetail();
      refreshOpenOrdersCount();
    } catch (error) {
      showError(error.response?.data?.error || t('orders.failedToCancelItem'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleClientCancel = async (groupId) => {
    await runGroupAction(async () => {
      try {
        await axios.post(`/api/orders/${groupId}/client-cancel`);
        await fetchDetail();
      } catch (error) {
        showError(error.response?.data?.error || 'Failed to cancel order');
      }
    });
  };

  const handleGroupDelivered = async () => {
    if (!detailGroup?._id) return;
    if (!(await confirm(t('orders.confirmSetDelivered')))) {
      return;
    }
    await runGroupAction(async () => {
      try {
        await axios.patch(`/api/orders/${detailGroup._id}/status`, {
          status: ORDER_GROUP_STATUSES.MEMO,
        });
        await fetchDetail();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToMarkDelivered'));
      }
    });
  };

  const openItemAuditLog = async (item) => {
    setItemAuditRef(getItemRef(item));
    setItemAuditOpen(true);
    setItemAuditLogs([]);
    setItemAuditLoading(true);
    try {
      const res = await axios.get('/api/audit-logs', { params: { orderItem: item._id, limit: 100 } });
      setItemAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch item audit logs:', error);
    } finally {
      setItemAuditLoading(false);
    }
  };

  const closeItemAuditLog = () => {
    setItemAuditOpen(false);
    setItemAuditRef('');
  };

  // Audit log helpers
  const getAuditStatusLabel = (status) => {
    if (!status) return '';
    const allLabels = {
      [ORDER_STATUSES.REQUESTED]: 'Requested',
      [ORDER_STATUSES.APPROVED]: 'Approved',
      [ORDER_STATUSES.READY]: 'Ready',
      [ORDER_STATUSES.CANCELED]: 'Cancelled',
      [ORDER_STATUSES.EXPIRED]: 'Expired',
      [ORDER_STATUSES.MEMO]: t('status.memo'),
      [ORDER_STATUSES.PAID]: 'Paid',
      [ORDER_GROUP_STATUSES.OPEN]: 'Open',
      [ORDER_GROUP_STATUSES.READY]: 'Ready',
      [ORDER_GROUP_STATUSES.MEMO]: t('status.memo'),
      [ORDER_GROUP_STATUSES.PAID]: 'Paid',
      [ORDER_GROUP_STATUSES.CANCELLED]: 'Cancelled',
    };
    return allLabels[status] || status;
  };

  const formatAuditAction = (action) => {
    const labels = {
      order_created: 'Order Created',
      order_status_change: 'Order Status Change',
      item_status_change: 'Item Status Change',
      item_returned_to_inventory: 'Returned to Inventory',
      item_price_updated: 'Price/Weight Updated',
      order_client_changed: 'Client Changed',
      order_note_updated: 'Order Notes Updated',
    };
    return labels[action] || action;
  };

  const getAuditActionColor = (action) => {
    switch (action) {
      case 'order_created': return 'primary';
      case 'order_status_change': return 'warning';
      case 'item_status_change': return 'info';
      case 'item_returned_to_inventory': return 'success';
      case 'item_price_updated': return 'info';
      case 'order_client_changed': return 'secondary';
      case 'order_note_updated': return 'default';
      default: return 'default';
    }
  };

  const formatAuditDetails = (log) => {
    const parts = [];
    if (log.details?.itemType) {
      parts.push(log.details.itemType === DIAMOND_TYPES.NON_CERTIFIED ? 'Non-certified' : 'Certified');
    }
    if (log.details?.returnedToInventory) parts.push('Returned to inventory');
    if (log.details?.itemCount != null) parts.push(`${log.details.itemCount} items`);
    if (log.details?.totalPrice != null) parts.push(`$${Number(log.details.totalPrice).toLocaleString()}`);
    if (log.details?.finalPrice != null) parts.push(`$${Number(log.details.finalPrice).toLocaleString()}`);
    if (log.details?.previousPricePerCarat != null || log.details?.newPricePerCarat != null) {
      const from = log.details.previousPricePerCarat != null ? `$${Number(log.details.previousPricePerCarat).toLocaleString()}` : '—';
      const to = log.details.newPricePerCarat != null ? `$${Number(log.details.newPricePerCarat).toLocaleString()}` : '—';
      parts.push(`Price/ct: ${from} → ${to}`);
    }
    if (log.details?.previousReservedCarat != null || log.details?.newReservedCarat != null) {
      const from = log.details.previousReservedCarat != null ? `${log.details.previousReservedCarat}` : '—';
      const to = log.details.newReservedCarat != null ? `${log.details.newReservedCarat}` : '—';
      parts.push(`Weight: ${from} → ${to} ct`);
    }
    if (log.details?.fromClientName && log.details?.toClientName) {
      parts.push(`${log.details.fromClientName} → ${log.details.toClientName}`);
    }
    if (log.action === 'order_note_updated') {
      if (log.details?.previousNote) parts.push(`Was: ${log.details.previousNote}`);
      if (log.details?.newNote) parts.push(`Now: ${log.details.newNote}`);
    }
    return parts.join(' | ') || '—';
  };

  const handleOrderMovedToClient = (data) => {
    setDetailGroup(data.order);
    setDetailItems(data.items || []);
    const name = getClientDisplayName(data.order?.clientUser);
    showSuccess(t('orders.moveToClientSuccess', { name: name || '—' }));
    if (detailTab === 1) fetchAuditLogs(id);
  };

  const buildExportConfig = () => {
    const columns = [
      t('orderItems.columns.certNumber'),
      t('orderItems.columns.description'),
      t('orderItems.columns.weight'),
      'Price per ct',
      'Price',
    ];
    const sortedItems = sortOrderItemsByCreatedAt(detailItems);
    const rows = sortedItems.map((item) => [
      getItemExportTypeLabel(item, t),
      getOrderItemDescriptionCell(item),
      getItemDetails(item, t),
      getItemPricePerCt(item) === '—' ? '—' : `${getItemPricePerCt(item)}/${isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct'}`,
      getItemDisplayPrice(item),
    ]);
    const totalWeight = sortedItems.reduce((sum, item) => sum + (getItemCarat(item) || 0), 0);
    const totalPrice = Number(detailGroup.totalPrice || 0);
    const orderCurrency = detailGroup.totalPriceCurrency || 'USD';
    const totalRow = ['', 'TOTAL', `${totalWeight.toFixed(2)} ct`, '', formatCurrency(totalPrice, orderCurrency)];
    const orderNum = detailGroup._id?.slice(-6).toUpperCase() || 'ORDER';
    return {
      title: `Order #${orderNum}`,
      subtitle: detailGroup.clientNote
        ? `${t('orders.noteForEmployee')}: ${detailGroup.clientNote}`
        : undefined,
      filename: `order-${orderNum}.pdf`,
      columns,
      rows,
      totalRow,
    };
  };

  if (!id) {
    return (
      <OrderDetailPageShell embedded={embedded}>
        <Typography>Invalid order.</Typography>
      </OrderDetailPageShell>
    );
  }

  if (detailLoading && !detailGroup) {
    return (
      <OrderDetailPageShell embedded={embedded}>
        <Typography sx={{ py: 3 }}>Loading order...</Typography>
      </OrderDetailPageShell>
    );
  }

  if (!detailGroup) {
    return (
      <OrderDetailPageShell embedded={embedded}>
        <Typography sx={{ py: 3 }}>Order not found.</Typography>
        <Button startIcon={<BackIcon />} onClick={goBack}>{t(backTarget.labelKey)}</Button>
      </OrderDetailPageShell>
    );
  }

  return (
    <OrderDetailPageShell embedded={embedded}>
        {embedded ? (
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Box>
                <Typography variant="h6" component="h1">
                  {t('orders.orderNumber')}{getGroupOrderNumber(detailGroup)}
                </Typography>
                <Chip
                  label={getGroupStatusLabel(detailGroup.status)}
                  color={getGroupStatusColor(detailGroup.status)}
                  size="small"
                  sx={{ mt: 0.75 }}
                />
              </Box>
              {navIds.length > 1 && navIndex >= 0 && (
                <ListNavigation ids={navIds} index={navIndex} onPrev={goToPrev} onNext={goToNext} />
              )}
            </Box>
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                {t('orders.items')}: <strong>{detailGroup.totalItems || detailGroup.itemCount}</strong>
                {' · '}{t('orders.totalPrice')}: <strong>{formatCurrency(detailGroup.totalPrice || 0, detailGroup.totalPriceCurrency || 'USD')}</strong>
                {isInternal && (<>{' · '}{t('orders.createdBy')}: <strong>{getCreatedByName(detailGroup)}</strong></>)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {getGroupDate(detailGroup)}
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Button startIcon={<BackIcon />} onClick={goBack} size="small">
            {t(backTarget.labelKey)}
          </Button>
          <ListNavigation ids={navIds} index={navIndex} onPrev={goToPrev} onNext={goToNext} />
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" component="h1">
                Order {getGroupOrderNumber(detailGroup)}
              </Typography>
              <Chip
                label={getGroupStatusLabel(detailGroup.status)}
                color={getGroupStatusColor(detailGroup.status)}
                size="small"
                sx={{ mt: 1 }}
              />
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {isInternal && (<>Client:{' '}
                    {detailGroup.clientUser?._id ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(getClientTabPath(detailGroup.clientUser._id, 'items'))}
                        sx={{ fontWeight: 'bold', verticalAlign: 'baseline' }}
                      >
                        {getGroupClientName(detailGroup)}
                      </Link>
                    ) : <strong>{getGroupClientName(detailGroup)}</strong>}
                    {' | '}</>)}
                  Items: <strong>{detailGroup.totalItems || detailGroup.itemCount}</strong>
                  {' | '}Total: <strong>{formatCurrency(detailGroup.totalPrice || 0, detailGroup.totalPriceCurrency || 'USD')}</strong>
                  {isInternal && (<>{' | '}Created by: <strong>{getCreatedByName(detailGroup)}</strong></>)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getGroupDate(detailGroup)}
                </Typography>
                {isInternal && (detailGroup.clientUser?.clientTags || []).length > 0 && (
                  <ClientTagsRow tags={detailGroup.clientUser.clientTags} maxVisible={6} sx={{ mt: 0.75 }} />
                )}
              </Box>

              {/* Client note */}
              {noteEditing ? (
                <Box mt={1.5} display="flex" alignItems="flex-start" gap={1}>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={5}
                    fullWidth
                    label={t('orders.noteForEmployee')}
                    value={noteEditValue}
                    onChange={(e) => setNoteEditValue(e.target.value)}
                    inputProps={{ maxLength: 1000 }}
                    sx={{ maxWidth: 500 }}
                  />
                  <Button size="small" variant="contained" onClick={handleSaveNote} disabled={noteSaving}>{t('common.save')}</Button>
                  <Button size="small" onClick={() => { setNoteEditing(false); setNoteEditValue(detailGroup.clientNote || ''); }}>{t('common.cancel')}</Button>
                </Box>
              ) : (
                <Box mt={1}>
                  {detailGroup.clientNote ? (
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 500 }}>
                        <strong>{t('orders.noteForEmployee')}:</strong>{' '}{detailGroup.clientNote}
                      </Typography>
                      <IconButton size="small" onClick={() => { setNoteEditValue(detailGroup.clientNote || ''); setNoteEditing(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditIcon />}
                      onClick={() => setNoteEditing(true)}
                      sx={{ color: 'text.secondary', textTransform: 'none', p: 0 }}
                    >
                      {t('orders.addNote')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
            {canTransferOrderClient && detailGroup.clientUser?._id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MoveClientIcon />}
                onClick={() => setMoveClientDialogOpen(true)}
              >
                {t('orders.moveToClient')}
              </Button>
            )}
          </Box>
        </Paper>
          </>
        )}

        {isInternal && (
          <Tabs value={detailTab} onChange={handleDetailTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab label="Items" />
            <Tab label="Audit Log" icon={<HistoryIcon fontSize="small" />} iconPosition="start" />
          </Tabs>
        )}

        {detailTab === 0 ? (
          <>
          {canSelectOrderItems && selectedItemIds.size > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="body2" fontWeight="bold">
                  {t('clients.selectedCountSubtotal', {
                    count: selectedItemIds.size,
                    subtotal: selectedSubtotalLabel,
                  })}
                </Typography>
                {selectedCancellableItems.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={buttonStartIcon(cancelLoading, CancelIcon)}
                    onClick={openBulkCancelSelected}
                    disabled={cancelLoading}
                  >
                    {t('checks.cancelSelectedItems', { count: selectedCancellableItems.length })}
                  </Button>
                )}
              </Box>
            </Paper>
          )}
          {(!isInternal || isMobile) ? (
            <>
            <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
              <ExportPdfButton exportConfig={buildExportConfig()} disabled={detailItems.length === 0} />
              <ExportCsvButton exportConfig={buildExportConfig()} disabled={detailItems.length === 0} />
            </Box>
            {canSelectOrderItems && detailItems.length > 0 && (
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={allOrderItemsSelected}
                    indeterminate={someOrderItemsSelected}
                    onChange={toggleSelectAllOrderItems}
                  />
                )}
                label={t('clients.selectAllOnPage')}
                sx={{ mb: 1 }}
              />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {detailItems.map((item) => {
                const edits = itemEdits[item._id];
                const isNonCert = item.type === DIAMOND_TYPES.NON_CERTIFIED;
                const showPricePerCtInput = canEditOrderItemPrice;
                const showWeightInput = canEditOrderItemWeight && (isNonCert || isCustomByWeightItem(item));
                const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
                const displayWeight = showWeightInput && edits?.weight !== undefined
                  ? edits.weight
                  : ((isNonCert || isCustomByWeightItem(item)) ? item.weight : null);
                const isSelected = selectedItemIds.has(item._id);
                return (
                  <Card key={item._id} variant="outlined" sx={{
                    overflow: 'visible',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                  }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                            {canSelectOrderItems && canCancelOrderDetailItem(item) && (
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => toggleOrderItemSelect(item._id)}
                                sx={{ p: 0 }}
                                inputProps={{ 'aria-label': t('checks.selectItem') }}
                              />
                            )}
                            <Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" />
                            {isNonCertByCountItem(item) && (
                              <Chip label={t('inventory.orderedByCount')} size="small" color="info" />
                            )}
                            <Chip label={getItemStatusLabel(item.status)} color={getItemStatusColor(item.status)} size="small" />
                          </Box>
                          {item.type === DIAMOND_TYPES.CERTIFIED ? (
                            <Box>
                              <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" sx={{ fontWeight: 'bold' }} />
                              {getCertifiedDiamondSpecsWithWeight(item) !== '—' && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {getCertifiedDiamondSpecsWithWeight(item)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                          )}
                          <OrderItemNotes item={item} sx={{ mt: 0.5 }} />
                          {isNonCertByCountItem(item) && (
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold">
                              {getNonCertByCountStoneLabel(item, t)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {showWeightInput ? (
                              <Box component="span" display="inline-flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                                {isNonCertByCountItem(item) && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    {isEstimatedNonCertWeight(item)
                                      ? t('inventory.estimatedWeight')
                                      : t('inventory.actualWeight')}:
                                  </Typography>
                                )}
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0.01, step: 0.01 }}
                                  value={displayWeight ?? ''}
                                  onChange={(e) => setItemEdit(item._id, 'weight', e.target.value)}
                                  onBlur={() => (edits?.weight !== undefined ? handleSaveItemEdit(item) : null)}
                                  disabled={savingItemId === item._id}
                                  sx={{ width: 72 }}
                                />
                                {weightSuffix}
                              </Box>
                            ) : (
                              isNonCertByCountItem(item) ? getNonCertWeightLabel(item, t) : getItemDetails(item)
                            )}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          {showPricePerCtInput ? (
                            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                              <Typography component="span" color="text.secondary">{`$/${weightSuffix}`}</Typography>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                value={edits?.pricePerUnit !== undefined ? edits.pricePerUnit : (item.pricePerUnit != null ? item.pricePerUnit : (getItemCarat(item) > 0 && item.finalPrice != null ? item.finalPrice / getItemCarat(item) : ''))}
                                onChange={(e) => setItemEdit(item._id, 'pricePerUnit', e.target.value)}
                                onBlur={() => (edits?.pricePerUnit !== undefined ? handleSaveItemEdit(item) : null)}
                                disabled={savingItemId === item._id}
                                sx={{ width: 88 }}
                              />
                              {savingItemId === item._id && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {getItemPricePerCt(item) === '—' ? '—' : `${getItemPricePerCt(item)}/${weightSuffix}`}
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight="bold" color="primary.main">{getItemDisplayPrice(item, edits)}</Typography>
                        </Box>
                      </Box>
                      {isInternal && (
                        <Box display="flex" gap={0.5} justifyContent="flex-end" flexWrap="wrap" sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          {canApproveReservations && item.status === ORDER_STATUSES.REQUESTED && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" color="primary" aria-label="Approve" data-testid="order-item-approve" onClick={() => handleItemApprove(detailGroup._id, item._id)} disabled={isPending(`approve-${item._id}`)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                  {iconButtonContent(isPending(`approve-${item._id}`), <ApproveIcon fontSize="small" />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" aria-label={t('orders.cancelItem')} data-testid="order-item-cancel" onClick={() => openCancelDialog('item', detailGroup._id, item._id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {[ORDER_STATUSES.APPROVED, ORDER_STATUSES.READY].includes(item.status) && (
                            <Tooltip title="Mark Ready">
                              <IconButton size="small" color="primary" onClick={() => handleItemReady(detailGroup._id, item._id)} disabled={isPending(`ready-${item._id}`)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                {iconButtonContent(isPending(`ready-${item._id}`), <ReadyIcon fontSize="small" />)}
                              </IconButton>
                            </Tooltip>
                          )}
                          {[ORDER_STATUSES.READY, ORDER_STATUSES.APPROVED].includes(item.status) && canChangeStatus && (
                            <Tooltip title={t('orders.markAsDelivered')}>
                              <IconButton size="small" color="warning" onClick={() => handleItemToDelivered(detailGroup._id, item._id)} disabled={isPending(`deliver-${item._id}`)}>
                                {iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}
                              </IconButton>
                            </Tooltip>
                          )}
                          {item.status === ORDER_STATUSES.MEMO && canChangeStatus && (
                            <Tooltip title={t('orders.undoDelivered')}>
                              <IconButton size="small" color="primary" onClick={() => handleItemUndoDelivered(detailGroup._id, item._id)} disabled={isPending(`undo-deliver-${item._id}`)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                {iconButtonContent(isPending(`undo-deliver-${item._id}`), <ReadyIcon fontSize="small" />)}
                              </IconButton>
                            </Tooltip>
                          )}
                          {isInternal && (
                            <Tooltip title="Item Audit Log">
                              <IconButton size="small" onClick={() => openItemAuditLog(item)}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {detailItems.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">No items.</Typography>
              )}
            </Box>
            </>
          ) : (
          <>
            <Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
              <ExportPdfButton exportConfig={buildExportConfig()} disabled={detailItems.length === 0} />
              <ExportCsvButton exportConfig={buildExportConfig()} disabled={detailItems.length === 0} />
            </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canSelectOrderItems && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allOrderItemsSelected}
                        indeterminate={someOrderItemsSelected}
                        onChange={toggleSelectAllOrderItems}
                        inputProps={{ 'aria-label': t('clients.selectAllOnPage') }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Type</TableCell>
                  <TableCell>{t('orderItems.columns.description')}</TableCell>
                  <TableCell>{t('orderItems.columns.weight')}</TableCell>
                  <TableCell>Price per ct</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  {isInternal && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {detailItems.map((item) => {
                  const edits = itemEdits[item._id];
                  const isNonCert = item.type === DIAMOND_TYPES.NON_CERTIFIED;
                  const showPricePerCtInput = canEditOrderItemPrice;
                  const showWeightInput = canEditOrderItemWeight && (isNonCert || isCustomByWeightItem(item));
                  const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
                  const displayWeight = showWeightInput && edits?.weight !== undefined
                    ? edits.weight
                    : ((isNonCert || isCustomByWeightItem(item)) ? item.weight : null);

                  const isSelected = selectedItemIds.has(item._id);
                  return (
                    <TableRow key={item._id} hover selected={isSelected}>
                      {canSelectOrderItems && (
                        <TableCell padding="checkbox">
                          {canCancelOrderDetailItem(item) ? (
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleOrderItemSelect(item._id)}
                              inputProps={{ 'aria-label': t('checks.selectItem') }}
                            />
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" />
                          {isNonCertByCountItem(item) && (
                            <Chip label={t('inventory.orderedByCount')} size="small" color="info" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.type === DIAMOND_TYPES.CERTIFIED ? (
                          <Box>
                            <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                            {getCertifiedDiamondSpecsWithWeight(item) !== '—' && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {getCertifiedDiamondSpecsWithWeight(item)}
                              </Typography>
                            )}
                            <OrderItemNotes item={item} />
                          </Box>
                        ) : (
                          <>
                            {getItemRef(item)}
                            <OrderItemNotes item={item} />
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        {isNonCertByCountItem(item) && (
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {getNonCertByCountStoneLabel(item, t)}
                          </Typography>
                        )}
                        {showWeightInput ? (
                          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                            {isNonCertByCountItem(item) && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {isEstimatedNonCertWeight(item)
                                  ? t('inventory.estimatedWeight')
                                  : t('inventory.actualWeight')}:
                              </Typography>
                            )}
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0.01, step: 0.01 }}
                              value={displayWeight ?? ''}
                              onChange={(e) => setItemEdit(item._id, 'weight', e.target.value)}
                              onBlur={() => (edits?.weight !== undefined ? handleSaveItemEdit(item) : null)}
                              disabled={savingItemId === item._id}
                              sx={{ width: 80 }}
                            />
                            <Typography component="span" variant="caption">{weightSuffix}</Typography>
                          </Box>
                        ) : (
                          isNonCertByCountItem(item) ? getNonCertWeightLabel(item, t) : getItemDetails(item)
                        )}
                      </TableCell>
                      <TableCell>
                        {showPricePerCtInput ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography component="span" color="text.secondary">{`$/${weightSuffix}`}</Typography>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, step: 1 }}
                              value={edits?.pricePerUnit !== undefined ? edits.pricePerUnit : (item.pricePerUnit != null ? item.pricePerUnit : (getItemCarat(item) > 0 && item.finalPrice != null ? item.finalPrice / getItemCarat(item) : ''))}
                              onChange={(e) => setItemEdit(item._id, 'pricePerUnit', e.target.value)}
                              onBlur={() => (edits?.pricePerUnit !== undefined ? handleSaveItemEdit(item) : null)}
                              disabled={savingItemId === item._id}
                              sx={{ width: 100 }}
                            />
                            {savingItemId === item._id && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                          </Box>
                        ) : (
                          getItemPricePerCt(item) === '—' ? '—' : `${getItemPricePerCt(item)}/${weightSuffix}`
                        )}
                      </TableCell>
                      <TableCell>{getItemDisplayPrice(item, edits)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getItemStatusLabel(item.status)}
                          color={getItemStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      {isInternal && (
                      <TableCell align="right">
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {canApproveReservations && item.status === ORDER_STATUSES.REQUESTED && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" color="primary" aria-label="Approve" data-testid="order-item-approve" onClick={() => handleItemApprove(detailGroup._id, item._id)} disabled={isPending(`approve-${item._id}`)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                  {iconButtonContent(isPending(`approve-${item._id}`), <ApproveIcon fontSize="small" />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" aria-label={t('orders.cancelItem')} data-testid="order-item-cancel" onClick={() => openCancelDialog('item', detailGroup._id, item._id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {canChangeStatus && item.status === ORDER_STATUSES.APPROVED && (
                            <>
                              <Tooltip title="Ready">
                                <IconButton size="small" color="success" onClick={() => handleItemReady(detailGroup._id, item._id)} disabled={isPending(`ready-${item._id}`)} sx={{ border: '1px solid', borderColor: 'success.main' }}>
                                  {iconButtonContent(isPending(`ready-${item._id}`), <ReadyIcon fontSize="small" />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('orders.markAsDelivered')}>
                                <IconButton size="small" color="warning" onClick={() => handleItemToDelivered(detailGroup._id, item._id)} disabled={isPending(`deliver-${item._id}`)} sx={{ border: '1px solid', borderColor: 'warning.main' }}>
                                  {iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" data-testid="order-item-cancel" onClick={() => openCancelDialog('item', detailGroup._id, item._id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {canChangeStatus && item.status === ORDER_STATUSES.READY && (
                            <>
                              <Tooltip title={t('orders.markAsDelivered')}>
                                <IconButton size="small" color="warning" onClick={() => handleItemToDelivered(detailGroup._id, item._id)} disabled={isPending(`deliver-${item._id}`)} sx={{ border: '1px solid', borderColor: 'warning.main' }}>
                                  {iconButtonContent(isPending(`deliver-${item._id}`), <DeliverActionIcon />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" data-testid="order-item-cancel" onClick={() => openCancelDialog('item', detailGroup._id, item._id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {canChangeStatus && item.status === ORDER_STATUSES.MEMO && (
                            <>
                              <Tooltip title={t('orders.undoDelivered')}>
                                <IconButton size="small" color="primary" onClick={() => handleItemUndoDelivered(detailGroup._id, item._id)} disabled={isPending(`undo-deliver-${item._id}`)} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
                                  {iconButtonContent(isPending(`undo-deliver-${item._id}`), <ReadyIcon fontSize="small" />)}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" data-testid="order-item-cancel" onClick={() => openCancelDialog('item', detailGroup._id, item._id)} sx={{ border: '1px solid', borderColor: 'error.main' }}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {isInternal && (
                            <Tooltip title="Item Audit Log">
                              <IconButton size="small" onClick={() => openItemAuditLog(item)}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {detailItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={(canSelectOrderItems ? 1 : 0) + 6 + (isInternal ? 1 : 0)} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No items.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </>
          )}
          </>
        ) : (
          <>
            {auditLoading ? (
              <Typography sx={{ py: 3 }}>Loading audit logs...</Typography>
            ) : auditLogs.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">No audit logs found.</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log._id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.createdAt ? formatDateTime(log.createdAt) : '—'}</TableCell>
                        <TableCell>
                          <Chip label={formatAuditAction(log.action)} size="small" color={getAuditActionColor(log.action)} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {log.performedBy ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email : '—'}
                        </TableCell>
                        <TableCell>
                          <AuditLogClientLinkCell log={log} />
                        </TableCell>
                        <TableCell>
                          {log.diamond ? (log.diamond.certificateNumber || `${log.diamond.shape || ''} ${log.diamond.carat || ''}ct`.trim()) : log.lot ? `${log.lot.shape || ''} ${log.lot.measurement || ''}`.trim() || 'Lot' : '—'}
                        </TableCell>
                        <TableCell>
                          <AuditLogFromCell log={log} getAuditStatusLabel={getAuditStatusLabel} />
                        </TableCell>
                        <TableCell>
                          <AuditLogToCell log={log} getAuditStatusLabel={getAuditStatusLabel} getToChipColor={getItemStatusColor} />
                        </TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{formatAuditDetails(log)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
          {canChangeStatus && [ORDER_GROUP_STATUSES.OPEN, ORDER_GROUP_STATUSES.READY].includes(detailGroup.status) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={buttonStartIcon(groupActionLoading, DeliverActionIcon, { fontSize: 'inherit' })}
              onClick={handleGroupDelivered}
              disabled={groupActionLoading}
              size="small"
            >
              {groupActionLoading ? t('common.saving') : t('orders.setAllToDelivered')}
            </Button>
          )}
          {canClientCancel(detailGroup) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={buttonStartIcon(groupActionLoading, CancelIcon)}
              onClick={() => handleClientCancel(detailGroup._id)}
              disabled={groupActionLoading}
              size="small"
            >
              Cancel Order
            </Button>
          )}
          {canChangeStatus && [ORDER_GROUP_STATUSES.OPEN, ORDER_GROUP_STATUSES.READY, ORDER_GROUP_STATUSES.MEMO].includes(detailGroup.status) && (
            <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => openCancelDialog('group', detailGroup._id)} size="small">
              Cancel Order
            </Button>
          )}
        </Box>

        <BulkCancelOrderItemsDialog
          open={cancelDialogOpen}
          onClose={closeCancelDialog}
          items={cancelItems}
          onItemsChange={setCancelItems}
          onConfirm={confirmCancel}
          loading={cancelLoading}
          isMobile={isMobile}
          t={t}
          titleKey={cancelTarget?.type === 'group' ? 'orders.cancelEntireOrder' : 'orders.cancelItem'}
          bodyKey={cancelTarget?.type === 'group' ? 'orders.cancelOrderBody' : 'orders.cancelItemBody'}
          confirmKey={
            cancelTarget?.type === 'group'
              ? 'orders.cancelOrder'
              : (cancelItems.length > 1 ? 'checks.cancelSelectedItems' : 'orders.cancelItem')
          }
        />

        {/* Item audit log dialog */}
        <Dialog open={itemAuditOpen} onClose={closeItemAuditLog} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="action" />
              <Typography variant="h6">Item Audit Log</Typography>
              {itemAuditRef && <Chip label={itemAuditRef} size="small" variant="outlined" sx={{ ml: 1 }} />}
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: 200 }}>
            {itemAuditLoading ? (
              <Typography sx={{ py: 3 }}>Loading audit logs...</Typography>
            ) : itemAuditLogs.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">No audit logs found for this item.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Performed By</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemAuditLogs.map((log) => (
                      <TableRow key={log._id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.createdAt ? formatDateTime(log.createdAt) : '—'}</TableCell>
                        <TableCell><Chip label={formatAuditAction(log.action)} size="small" color={getAuditActionColor(log.action)} variant="outlined" /></TableCell>
                        <TableCell>{log.performedBy ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email : '—'}</TableCell>
                        <TableCell><AuditLogFromCell log={log} getAuditStatusLabel={getAuditStatusLabel} /></TableCell>
                        <TableCell><AuditLogToCell log={log} getAuditStatusLabel={getAuditStatusLabel} getToChipColor={getItemStatusColor} /></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{formatAuditDetails(log)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions><Button onClick={closeItemAuditLog}>Close</Button></DialogActions>
        </Dialog>

        <MoveOrderClientDialog
          open={moveClientDialogOpen}
          onClose={() => setMoveClientDialogOpen(false)}
          orderId={id}
          currentClientId={detailGroup.clientUser?._id}
          onSuccess={handleOrderMovedToClient}
        />

        <CheckoutLeftoversDialog
          open={leftoversOpen}
          rows={leftoversRows}
          onClose={closeLeftovers}
        />
    </OrderDetailPageShell>
  );
};

export default OrderDetail;
